/**
 * The route TYPE and the matcher, shared by both public apps.
 *
 * The tables themselves are not here — landing owns its routes and gtm owns its routes, which is
 * the whole point of the split. What is shared is the shape they both have to satisfy and the one
 * matching rule, because two copies of `match()` that disagree about a trailing slash is a bug
 * nobody would find until a prerendered page and its hydration picked different components.
 */
import type { ComponentType } from "react"

export interface Route {
  path: string
  title: string
  description: string
  /** The component, not a call of it — the prerenderer and the client both need to render it
   *  as an element so its hooks have a dispatcher. */
  element: ComponentType
  /**
   * What this page needs in the document itself, as `{preloadKey: apiPath}`.
   *
   * The prerenderer fetches each one and serializes it into the page, so a crawler reads the
   * incidents, the profiles, the taxonomy and the figures rather than the word "Loading…".
   */
  loads?: Record<string, string>
  /** Expanded into one page per subject at build time — see `subjects` in prerender.mjs. */
  perSubject?: boolean
}

/** Match a pathname to a route, honouring `:param` segments. */
export function matchIn(routes: readonly Route[], pathname: string): Route | undefined {
  const path = pathname.replace(/\/+$/, "") || "/"
  const exact = routes.find((r) => r.path === path)
  if (exact) return exact
  const segments = path.split("/").filter(Boolean)
  return routes.find((r) => {
    const parts = r.path.split("/").filter(Boolean)
    if (parts.length !== segments.length) return false
    return parts.every((p, i) => p.startsWith(":") || p === segments[i])
  })
}

/**
 * THE URL CONTRACT, read from ../../public-paths.json — see the note inside that file.
 *
 * Re-exported here so app code gets typed constants, while each vite config reads the same JSON
 * directly. One file, two consumers, no chance of the dev proxy and the route tables disagreeing.
 */
import contract from "../../public-paths.json"

export const GTM_PREFIXES: readonly string[] = contract.owned.gtm
export const LANDING_PREFIXES: readonly string[] = contract.owned.landing
